// Connected · EntityLinkProvider
//
// Provides the real `EntityLink` renderer to the `EntityLinkContext`.
// Mount once near the Refine/Router app root so every `domain/` chip and
// `tables/` cell renders with navigation + hover popover.
//
// Without this provider the context falls back to a pure-visual `EntityChip`
// (no navigation, no popover) — useful in storybook/tests.

import React, { type ReactNode } from "react";
import {
  EntityLinkContextProvider,
  type EntityLinkRendererProps,
} from "@ds/display/EntityLinkContext";
import { EntityLink } from "@ds/connected/EntityLink";

function renderEntityLink(props: EntityLinkRendererProps): ReactNode {
  return (
    <EntityLink
      type={props.type as Parameters<typeof EntityLink>[0]["type"]}
      id={props.id}
      label={props.label}
      variant={props.variant}
      size={props.size}
      testId={props.testId}
    />
  );
}

export interface EntityLinkProviderProps {
  children: ReactNode;
}

/**
 * Mount once at the app root (inside Router + Refine providers).
 *
 * ```tsx
 * <EntityLinkProvider>
 *   <App />
 * </EntityLinkProvider>
 * ```
 */
export function EntityLinkProvider({ children }: EntityLinkProviderProps): React.ReactElement {
  return (
    <EntityLinkContextProvider renderer={renderEntityLink}>
      {children}
    </EntityLinkContextProvider>
  );
}
